import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import {
	borderNeutralModerate,
	neutralPrimary,
	primaryColor,
	textNeutralSecondary,
} from "../../../core/theme";
import { toStringArray } from "../../utils/toStringArray";
import { sanitizeNumericValues } from "../../utils/sanitizeNumericValues";

export interface MultiNumberInputProps {
	label?: string;
	placeholder?: string;
	name?: string;
	value?: (string | number)[] | string | number;
	onChange?: (value: string[]) => void;
}

const labelStyle: SxProps<Theme> = {
	display: "block",
	color: textNeutralSecondary,
	fontSize: "14px",
	lineHeight: "20px",
	marginBottom: "8px",
};

const inputStyle: SxProps<Theme> = {
	backgroundColor: "white",
	"& .MuiOutlinedInput-root": {
		borderRadius: 0,
		minHeight: "40px",
		padding: "2px 8px",
		color: neutralPrimary,
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: borderNeutralModerate,
		},
		"&:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: primaryColor,
		},
		"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: primaryColor,
			borderWidth: "2px",
		},
	},
};

const MultiNumberInput = ({
	label,
	placeholder,
	name,
	value,
	onChange = () => {},
}: MultiNumberInputProps) => {
	const currentValues = toStringArray(value);

	return (
		<Box>
			{label && (
				<Typography component="label" sx={labelStyle}>
					{label}
				</Typography>
			)}
			<Autocomplete
				data-testid={`multi_number_${name}`}
				multiple
				freeSolo
				options={[]}
				value={currentValues}
				onChange={(_event, newValue) =>
					onChange(
						sanitizeNumericValues(newValue as (string | number)[])
					)
				}
				renderTags={(values, getTagProps) =>
					values.map((v, index) => (
						<Chip
							{...getTagProps({ index })}
							key={`${name}_${v}`}
							label={v}
						/>
					))
				}
				sx={inputStyle}
				renderInput={(params) => (
					<TextField
						{...params}
						name={name}
						placeholder={placeholder}
						inputProps={{
							...params.inputProps,
							inputMode: "numeric",
							pattern: "[0-9]*",
						}}
					/>
				)}
			/>
		</Box>
	);
};

export default MultiNumberInput;
